import React from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from "@material-ui/core/styles";
import { InputLabel, MenuItem, Select, Typography, Checkbox, FormControlLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ActionButton from '../button/actionButton';
import TextField from '../input/textField';
import { client } from "../../utils/data/graphql/client";
import { createOtcOrderAdminMutation } from "../../utils/data/graphql/otc/mutations";
import { getAddSendFiatRecipientFormConfigurationAdminQuery } from "../../utils/data/graphql/otc/queries";
import { useAuth0 } from "@auth0/auth0-react";
import DynamicRecipientForm from '../form/DynamicRecipientForm';
import CircularLoader from '../loading/circularLoader';
import { DEPOSIT_CURRENCIES } from '../../utils/types';

const useStyles = makeStyles((theme) => ({
    root: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: '416px',
        maxHeight: '90vh',
        minHeight: '706px',
        borderRadius: '20px',
        background: theme.background.dialog,
        overflowY: 'auto',
    },
    cardContent: {
        margin: '32px !important',
        padding: '0 !important',
        display: 'flex',
        justifyContent: 'center',
    },
    backdrop: {
        ['@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))']: {
            '-webkit-backdrop-filter': 'blur(2em)',
            backdropFilter: 'blur(2em)',
            backgroundColor: 'rgb(0 0 0 / 33%) !important',
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '42px',
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(3),
    },
    heading: {
        marginTop: '12px',
        alignSelf: 'start'
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px 0px 32px 0px'
    },
    input: {
        margin: '12px 0px 12px 0px !important',
    },
    selectLabel: {
        marginBottom: '4px'
    },
    confirmButton: {
        marginBottom: '40px',
        position: 'relative',
    },
    autocompleteOption: {
        color: '#000000 !important',
        backgroundColor: '#ffffff !important',
        '&[data-focus="true"]': {
            backgroundColor: '#f5f5f5 !important',
        },
        '&[aria-selected="true"]': {
            backgroundColor: '#e0e0e0 !important',
        },
    },
    autocompleteListbox: {
        backgroundColor: '#ffffff !important',
        '& .MuiAutocomplete-option': {
            color: '#000000 !important',
        },
    },
    autoCalculateCheckbox: {
        marginTop: '8px',
        marginBottom: '8px',
        alignSelf: 'flex-start',
        '& .MuiTypography-body1': {
            fontSize: '13px',
            color: '#999',
        },
        '& .MuiCheckbox-root': {
            padding: '4px 9px',
        },
        '& .MuiCheckbox-root svg': {
            fontSize: '20px',
        },
        // Style for the checkmark path when checked
        '& .MuiCheckbox-root.Mui-checked svg path': {
            fill: '#ffffff',
        },
    },
}));

const SendFiatRecipientDetailsDialog = ({ onClose, onComplete, initialValues = {} }) => {
    const classes = useStyles();
    const { user } = useAuth0();
    const [submitAttempted, setSubmitAttempted] = React.useState(false);
    const [customerId, setCustomerId] = React.useState(initialValues.customerId || '');
    const [depositType] = React.useState(initialValues.depositType || 'BANK');
    
    // Source fields (which balance to deduct from)
    const [sourceCurrency, setSourceCurrency] = React.useState('USDC');
    const [sourceAmount, setSourceAmount] = React.useState('');
    
    // Payout fields (what recipient receives)
    const [payoutCurrency, setPayoutCurrency] = React.useState('USD');
    const [payoutAmount, setPayoutAmount] = React.useState('');
    const [quoteRate, setQuoteRate] = React.useState('');
    // const [midMarketRate, setMidMarketRate] = React.useState('');
    // const [bps, setBps] = React.useState('');
    const [transactionFee, setTransactionFee] = React.useState('');
    // const [grossProfit, setGrossProfit] = React.useState('');
    
    const [recipientFields, setRecipientFields] = React.useState({});
    const [isRecipientFormValid, setIsRecipientFormValid] = React.useState(false);
    
    // Track which field was last edited to prevent calculation loops
    const [lastEdited, setLastEdited] = React.useState(null);
    
    // Toggle for auto-calculation feature
    const [autoCalculate, setAutoCalculate] = React.useState(true);
    
    // Transaction date from initial values
    const [transactionDate] = React.useState(initialValues.date || new Date().toISOString().substring(0,10));
    const [configData, setConfigData] = React.useState(null);
    const [configLoading, setConfigLoading] = React.useState(true);
    const [configError, setConfigError] = React.useState(null);

    // Get currency list with popular ones first
    const currencies = React.useMemo(() => {
        // Define stablecoins to exclude from fiat sends
        const STABLECOINS = ['USDC', 'USDT', 'BUSD'];
        
        const allCurrencies = depositType === 'STABLECOIN' 
            ? ['USDT', 'USDC'] 
            : Object.keys(DEPOSIT_CURRENCIES || {}).filter(c => !STABLECOINS.includes(c)).sort();
        
        // Popular currencies to show first
        const popularCurrencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'HKD', 'JPY'];
        const popular = allCurrencies.filter(c => popularCurrencies.includes(c));
        const others = allCurrencies.filter(c => !popularCurrencies.includes(c));
        
        return [...popular, ...others];
    }, [depositType]);
    
    // Calculate quoteRate when payoutAmount changes
    React.useEffect(() => {
        if (autoCalculate && lastEdited === 'payoutAmount' && payoutAmount && sourceAmount && parseFloat(sourceAmount) > 0) {
            const calculated = (parseFloat(payoutAmount) / parseFloat(sourceAmount)).toFixed(5);
            setQuoteRate(calculated);
        }
    }, [payoutAmount, sourceAmount, autoCalculate, lastEdited]);

    // Calculate payoutAmount when quoteRate changes
    React.useEffect(() => {
        if (autoCalculate && lastEdited === 'quoteRate' && quoteRate && sourceAmount && parseFloat(quoteRate) > 0) {
            const calculated = (parseFloat(sourceAmount) * parseFloat(quoteRate)).toFixed(2);
            setPayoutAmount(calculated);
        }
    }, [quoteRate, sourceAmount, autoCalculate, lastEdited]);

    // Calculate based on sourceAmount changes
    React.useEffect(() => {
        if (autoCalculate && lastEdited === 'sourceAmount' && sourceAmount && parseFloat(sourceAmount) > 0) {
            if (quoteRate && parseFloat(quoteRate) > 0) {
                // If we have a quote rate, recalculate payout amount
                const calculated = (parseFloat(sourceAmount) * parseFloat(quoteRate)).toFixed(2);
                setPayoutAmount(calculated);
            } else if (payoutAmount && parseFloat(payoutAmount) > 0) {
                // If we have a payout amount, recalculate quote rate
                const calculated = (parseFloat(payoutAmount) / parseFloat(sourceAmount)).toFixed(5);
                setQuoteRate(calculated);
            }
        }
    }, [sourceAmount, quoteRate, payoutAmount, autoCalculate, lastEdited]);

    // Fetch dynamic form configuration using imperative client.query
    React.useEffect(() => {
        const fetchConfig = async () => {
            try {
                setConfigLoading(true);
                const result = await client.query({
                    query: getAddSendFiatRecipientFormConfigurationAdminQuery,
                    fetchPolicy: 'network-only'
                });
                setConfigData(result.data);
                setConfigError(null);
            } catch (error) {
                console.error('[SendRecipientDetailsDialog] Failed to load config:', error);
                setConfigError(error);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const isPositiveNumber = (val) => {
        const n = parseFloat(val);
        return !Number.isNaN(n) && n > 0;
    };

    const isFormValid = () => {
        return Boolean(
            customerId &&
            sourceCurrency &&
            isPositiveNumber(sourceAmount) &&
            payoutCurrency &&
            isPositiveNumber(payoutAmount) &&
            isRecipientFormValid
        );
    };

    const getValidationMessage = () => {
        if (!customerId) return 'Customer ID is required.';
        if (!sourceCurrency || !isPositiveNumber(sourceAmount)) return 'Please enter valid source currency and amount.';
        if (!payoutCurrency || !isPositiveNumber(payoutAmount)) return 'Please enter valid payout currency and amount.';
        if (!isRecipientFormValid) return 'Please complete all required recipient fields.';
        return '';
    };

    const handleRecipientValuesChange = (values, valid) => {
        setRecipientFields(values);
        setIsRecipientFormValid(valid);
    };

    if (configLoading) {
        return (
            <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
                <Fade in={true}>
                    <Card className={classes.root}>
                        <CardContent className={classes.cardContent}>
                            <div className={classes.content}>
                                <CircularLoader />
                            </div>
                        </CardContent>
                    </Card>
                </Fade>
            </Modal>
        );
    }

    if (configError) {
        return (
            <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
                <Fade in={true}>
                    <Card className={classes.root}>
                        <CardContent className={classes.cardContent}>
                            <ActionButton variant="secondary" size="lg"
                                          className={classes.closeButton}
                                          onClick={onClose}
                            >
                                Close
                            </ActionButton>
                            <div className={classes.content}>
                                <Typography variant="h3" className={classes.heading}>Error</Typography>
                                <Typography variant="body1" style={{ marginTop: '8px', color: '#d32f2f' }}>
                                    Failed to load form configuration. Please try again.
                                </Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Fade>
            </Modal>
        );
    }

    return (
        <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
            <Fade in={true}>
                <Card className={classes.root}>
                    <CardContent className={classes.cardContent}>
                        <ActionButton variant="secondary" size="lg"
                                      className={classes.closeButton}
                                      onClick={onClose}
                        >
                            Close
                        </ActionButton>
                        <div className={classes.content}>
                            <Typography variant="h3" className={classes.heading}>Create a pending send transaction</Typography>
                            <Typography variant="body1" style={{ alignSelf: 'start', marginTop: '8px' }}>
                                Enter the transaction details below.
                            </Typography>
                            <div className={classes.inputContainer}>
                                <TextField
                                    className={classes.input}
                                    name='customerId'
                                    type='text'
                                    value={customerId}
                                    label={'Customer ID'}
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setCustomerId(e.target.value)}
                                />
                                
                                {/* Source Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Source (Balance Deduction)
                                </Typography>
                                <div className={classes.input}>
                                    <InputLabel className={classes.selectLabel}>Source Currency *</InputLabel>
                                    <Select
                                        fullWidth
                                        value={sourceCurrency}
                                        onChange={(e) => setSourceCurrency(e.target.value)}
                                        style={{ color: '#ffffff' }}
                                    >
                                        <MenuItem value="USDC">USDC</MenuItem>
                                        <MenuItem value="USDT">USDT</MenuItem>
                                    </Select>
                                </div>
                                <TextField
                                    className={classes.input}
                                    name='sourceAmount'
                                    type='number'
                                    value={sourceAmount}
                                    label={'Source Amount'}
                                    placeholder="100.00"
                                    InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                    onChange={(e) => {
                                        setSourceAmount(e.target.value);
                                        setLastEdited('sourceAmount');
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    error={submitAttempted && (!sourceAmount || parseFloat(sourceAmount) <= 0)}
                                    helperText={submitAttempted && (!sourceAmount || parseFloat(sourceAmount) <= 0) ? "Please enter a valid amount" : ""}
                                />
                                
                                {/* Payout Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Payout (Recipient Receives)
                                </Typography>
                                
                                {/* Auto-calculate toggle */}
                                <FormControlLabel
                                    className={classes.autoCalculateCheckbox}
                                    control={
                                        <Checkbox
                                            checked={autoCalculate}
                                            onChange={(e) => setAutoCalculate(e.target.checked)}
                                            size="small"
                                            style={{
                                                color: autoCalculate ? '#4caf50' : 'rgba(255, 255, 255, 0.3)',
                                            }}
                                        />
                                    }
                                    label="Auto-calculate between Quote Rate and Payout Amount"
                                />
                                <Autocomplete
                                    className={classes.input}
                                    style={{paddingLeft: '0px'}}
                                    options={currencies}
                                    value={payoutCurrency}
                                    onChange={(event, newValue) => setPayoutCurrency(newValue || 'USD')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Payout Currency"
                                            InputLabelProps={{shrink: false, disableAnimation: true}}
                                            placeholder="Type to search..."
                                            style={{paddingLeft: '16px'}}
                                        />
                                    )}
                                    freeSolo={false}
                                    disableClearable
                                    classes={{
                                        option: classes.autocompleteOption,
                                        listbox: classes.autocompleteListbox,
                                    }}
                                    ListboxProps={{
                                        style: {
                                            backgroundColor: '#ffffff',
                                            color: '#000000',
                                        }
                                    }}
                                />
                                <Typography variant="caption" style={{ 
                                    marginTop: '12px',
                                    marginBottom: '4px',
                                    fontSize: '11px',
                                    color: '#999'
                                }}>
                                    {autoCalculate ? "Auto-calculates with Quote Rate" : "Manual entry mode"}
                                </Typography>
                                <TextField
                                    className={classes.input}
                                    name='payoutAmount'
                                    type='number'
                                    value={payoutAmount}
                                    label={'Payout Amount'}
                                    placeholder="150.00"
                                    InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                    onChange={(e) => {
                                        setPayoutAmount(e.target.value);
                                        setLastEdited('payoutAmount');
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    error={submitAttempted && (!payoutAmount || parseFloat(payoutAmount) <= 0)}
                                    helperText={submitAttempted && (!payoutAmount || parseFloat(payoutAmount) <= 0) ? "Please enter a valid amount" : ""}
                                />
                                
                                <Typography variant="caption" style={{ 
                                    marginTop: '12px',
                                    marginBottom: '4px',
                                    fontSize: '11px',
                                    color: '#999'
                                }}>
                                    {autoCalculate ? "Auto-calculates Payout Amount when entered" : "Manual entry mode"}
                                </Typography>
                                <TextField
                                    className={classes.input}
                                    name='quoteRate'
                                    type='number'
                                    value={quoteRate}
                                    label={'Quote Rate'}
                                    placeholder="1.00"
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => {
                                        setQuoteRate(e.target.value);
                                        setLastEdited('quoteRate');
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                />
                                
                                {/* <TextField
                                    className={classes.input}
                                    name='midMarketRate'
                                    type='number'
                                    value={midMarketRate}
                                    label={'Mid-Market Rate'}
                                    placeholder="1.00"
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setMidMarketRate(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                /> */}
                                
                                {/* <TextField
                                    className={classes.input}
                                    name='bps'
                                    type='number'
                                    value={bps}
                                    label={'BPS'}
                                    placeholder="0"
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setBps(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                /> */}
                                
                                <TextField
                                    className={classes.input}
                                    name='transactionFee'
                                    type='number'
                                    value={transactionFee}
                                    label={'Transaction Fee (USD)'}
                                    placeholder="0.00"
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setTransactionFee(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                />
                                
                                {/* <TextField
                                    className={classes.input}
                                    name='grossProfit'
                                    type='number'
                                    value={grossProfit}
                                    label={'Gross Profit(USD)'}
                                    placeholder="0.00"
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setGrossProfit(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                /> */}
                                
                                {/* Recipient Details Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Recipient Details
                                </Typography>
                                {/* Dynamic Recipient Form */}
                                <DynamicRecipientForm 
                                    configuration={configData?.getOtcSendRecipientFormConfiguration_Admin}
                                    onValuesChange={handleRecipientValuesChange}
                                    submitAttempted={submitAttempted}
                                />
                            </div>
                            {submitAttempted && !isFormValid() && (
                              <Typography variant="body2" style={{ color: '#d32f2f', alignSelf: 'start', marginTop: 8 }}>
                                {getValidationMessage()}
                              </Typography>
                            )}
                            <ActionButton
                                variant="primary"
                                size="lg"
                                className={classes.confirmButton}
                                onClick={async () => {
                                    setSubmitAttempted(true);
                                    if (!isFormValid()) {
                                        return;
                                    }
                                    try {
                                        await client.mutate({
                                            mutation: createOtcOrderAdminMutation,
                                            variables: {
                                                input: {
                                                    customerId: customerId,
                                                    side: 'WITHDRAW',
                                                    currency: payoutCurrency,
                                                    amount: parseFloat(payoutAmount),
                                                    debitAmountUsd: parseFloat(sourceAmount),
                                                    settlementType: 'FIAT',
                                                    recipientDetails: {
                                                        recipientType: recipientFields.recipientType || 'BUSINESS',
                                                        ...recipientFields
                                                    },
                                                    settlementDetails: {
                                                        quoteRate: quoteRate ? String(quoteRate) : undefined,
                                                        // midMarketRate: midMarketRate ? String(midMarketRate) : undefined,
                                                        // bps: bps ? String(bps) : undefined,
                                                        transactionFee: transactionFee ? String(transactionFee) : undefined,
                                                        // grossProfit: grossProfit ? String(grossProfit) : undefined
                                                    },
                                                    createdBy: user?.email,
                                    metadata: {
                                        transactionDate: transactionDate,
                                        sourceCurrency: sourceCurrency
                                    }
                                                }
                                            }
                                        });
                                        onClose();
                                        onComplete && onComplete(true);
                                    } catch (e) {
                                        console.error(e);
                                        onClose();
                                        onComplete && onComplete(false);
                                    }
                                }}
                                disabled={!isFormValid()}
                            >
                                Done
                            </ActionButton>
                        </div>
                    </CardContent>
                </Card>
            </Fade>
        </Modal>
    )
}

export default SendFiatRecipientDetailsDialog;


